import { useCallback, useEffect } from 'react';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useGroupBy } from '../../group-by';
import { useHierarchyStore } from '../../hierarchy';
import { useBulkSelectionStore } from '../bulk-selection-store';
import { useSelectCollapsedGroups } from '../select-collapsed-groups';
import { getAllIssuesIds } from '../utils';

export const useSelectAllCheckbox = () => {
	const [
		{
			hierarchyData: { data, isLoadingNextPage },
			childrenItems,
		},
	] = useHierarchyStore();

	const [
		{ areAllIssuesSelected, selectedIssues },
		{ selectAllIssues, selectAll, clearSelection, unselectAll },
	] = useBulkSelectionStore();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fetchingGroups, selectCollapsedGroups } = useSelectCollapsedGroups();
	const [groupByFieldId] = useGroupBy();
	const prevGroupByFieldId = usePreviousWithInitial(groupByFieldId);
	const isGrouping = groupByFieldId != null;

	useEffect(() => {
		if (prevGroupByFieldId !== groupByFieldId) {
			// if the groupByFieldId is updated, clear the selection
			clearSelection();
		}
	}, [clearSelection, groupByFieldId, prevGroupByFieldId]);

	/*
        UNSELECT - if is all selected and we get new data we should unselect the "select all" checkbox
        to avoid comparing all the data we can just update the select all once we:
        - fetch a new page
        - fetch any children
    */
	useEffect(() => {
		if (isLoadingNextPage && areAllIssuesSelected) {
			unselectAll();
		}
	}, [areAllIssuesSelected, isLoadingNextPage, unselectAll]);

	useEffect(() => {
		if (areAllIssuesSelected) {
			if (!isGrouping) {
				unselectAll();
			} else if (isGrouping && !fetchingGroups) {
				unselectAll();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(childrenItems), data.length]);

	/*
        SELECT ALL - if all the issues are selected we should check the 'select all checkbox'
        to avoid comparing all the data we can just check if it's all selected when an issue is selected
    */
	useEffect(() => {
		if (!areAllIssuesSelected && selectedIssues.size) {
			// if is no grouping and there are more data selected we should check if all are selected.
			if (!isGrouping && selectedIssues.size >= data.length) {
				const allIssues = getAllIssuesIds({ data, childrenItems });
				if (allIssues.length === selectedIssues.size) {
					selectAll();
				}
			} else if (isGrouping) {
				// if some of the groups were not fetched and have issues, then not select all
				for (let i = 0; i < data.length; i += 1) {
					const group = data[i];
					const hasChildren = group.childSummary?.hasChildren;
					if (hasChildren && childrenItems[group.issueId] === undefined) {
						return;
					}
				}
				const allIssues = getAllIssuesIds({ data, childrenItems });
				if (allIssues.length === selectedIssues.size) {
					selectAll();
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIssues]);

	const onClickCheckbox = useCallback(
		({ isChecked }: { isChecked: boolean }) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', 'selectAll', {
				isChecked,
			});
			if (isChecked) {
				const allIssues = getAllIssuesIds({ data, childrenItems });
				// if it's an empty project we don't want to anything
				if (!allIssues.length && !isGrouping) return;
				selectAllIssues({ issues: allIssues });

				if (isGrouping) {
					selectCollapsedGroups(data);
				}
			} else {
				clearSelection();
			}
		},
		[
			childrenItems,
			clearSelection,
			createAnalyticsEvent,
			data,
			isGrouping,
			selectAllIssues,
			selectCollapsedGroups,
		],
	);

	return {
		isChecked: areAllIssuesSelected,
		isIndeterminate: !areAllIssuesSelected && selectedIssues.size > 0,
		onClickCheckbox,
	};
};
